import { Button, Drawer, Empty, Flex, Skeleton, Tag, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { getFormatDate } from "../../../utils/date/get-date-safe.ts"
import { getEntityName } from "../../../utils/user/get-entity-name.ts"
import { getDocumentReviews } from "../../Project/api/project-attachment-workflow.services.ts"
import { type DocumentReview } from "../../Project/types/project-attachment-workflow.types.ts"
import { type FileItem } from "../types/file-item.types.ts"

const { Text, Title, Paragraph } = Typography

const REVIEW_STATUS_COLOR: Record<DocumentReview["status"], string> = {
  pending: "processing",
  approved: "success",
  rejected: "error"
}

interface ReviewDrawerProps {
  file: FileItem
  onClose: () => void
  onOpenDocument: () => void
  onAddComment: () => void
  onApprove: (review: DocumentReview) => Promise<void>
  onReject: (review: DocumentReview) => void
  /** Bumped by the parent after an approve/reject so the panel refetches. */
  refreshKey?: number
}

export function ReviewDrawer({
  file,
  onClose,
  onOpenDocument,
  onAddComment,
  onApprove,
  onReject,
  refreshKey
}: ReviewDrawerProps) {
  const { t } = useTranslation()
  const [review, setReview] = useState<DocumentReview | null | undefined>(undefined)
  const [loadFailed, setLoadFailed] = useState(false)
  const [approving, setApproving] = useState(false)
  const [reloadCount, setReloadCount] = useState(0)

  useEffect(() => {
    let cancelled = false
    setReview(undefined)
    setLoadFailed(false)
    void getDocumentReviews(file.id).then((response) => {
      if (cancelled) return
      if (!response.ok || !response.data) {
        setReview(null)
        setLoadFailed(true)
        return
      }
      // Ordered `-created` server-side — the first row is the latest review,
      // whichever version it names.
      setReview(response.data.results[0] ?? null)
    })
    return () => {
      cancelled = true
    }
  }, [file.id, refreshKey, reloadCount])

  const reviewerName = review?.reviewer ? getEntityName(review.reviewer) : null
  const requestedByName = review?.requested_by ? getEntityName(review.requested_by) : null

  return (
    // Default (portaled) container: this can open from the FileViewer modal
    // (via "Add comment"/"View review"), and an inline `getContainer={false}`
    // drawer would then paint behind that modal's own portal.
    <Drawer
      title={t("files.review.panelTitle")}
      open
      onClose={onClose}
      width={380}
      maskClosable
    >
      {review === undefined ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : loadFailed ? (
        <Empty description={t("files.review.loadFailed")}>
          <Button size="small" onClick={() => setReloadCount((count) => count + 1)}>
            {t("files.review.retry")}
          </Button>
        </Empty>
      ) : review === null ? (
        <Empty description={t("files.review.noReview")} />
      ) : (
        <Flex vertical gap="middle">
          <Flex vertical gap={0}>
            <Title level={5} className="!mb-0 break-all">
              {review.file_name}
            </Title>
            <Text type="secondary">
              {t("fileViewer.detailsPanel.versionShort", {
                version: review.attachment_version
              })}
            </Text>
          </Flex>

          <Flex vertical gap={4}>
            <Text type="secondary">{t("files.review.statusLabel")}</Text>
            <Tag color={REVIEW_STATUS_COLOR[review.status]} className="w-fit">
              {t(`files.review.status.${review.status}`)}
            </Tag>
          </Flex>

          {reviewerName && (
            <Flex vertical gap={4}>
              <Text type="secondary">{t("files.review.reviewer")}</Text>
              <Text>{reviewerName}</Text>
            </Flex>
          )}

          {requestedByName && (
            <Flex vertical gap={4}>
              <Text type="secondary">{t("files.review.requestedBy")}</Text>
              <Text>{requestedByName}</Text>
            </Flex>
          )}

          {review.due_date && (
            <Flex vertical gap={4}>
              <Text type="secondary">{t("files.review.dueDate")}</Text>
              <Text>{getFormatDate(review.due_date) ?? review.due_date}</Text>
            </Flex>
          )}

          {review.message && (
            <Flex vertical gap={4}>
              <Text type="secondary">{t("files.review.message")}</Text>
              <Paragraph className="!mb-0">{review.message}</Paragraph>
            </Flex>
          )}

          {review.status === "rejected" && review.rejection_reason && (
            <Flex vertical gap={4}>
              <Text type="secondary">{t("files.review.rejectReason")}</Text>
              <Paragraph className="!mb-0">{review.rejection_reason}</Paragraph>
            </Flex>
          )}

          <Flex gap="small" wrap="wrap">
            <Button size="small" onClick={onOpenDocument}>
              {t("files.review.openDocument")}
            </Button>
            <Button size="small" onClick={onAddComment}>
              {t("files.review.addComment")}
            </Button>
          </Flex>

          {review.status === "pending" && (
            <Flex gap="small">
              <Button
                type="primary"
                loading={approving}
                onClick={async () => {
                  setApproving(true)
                  await onApprove(review)
                  setApproving(false)
                }}
              >
                {t("files.review.approve")}
              </Button>
              <Button danger onClick={() => onReject(review)}>
                {t("files.review.reject")}
              </Button>
            </Flex>
          )}
        </Flex>
      )}
    </Drawer>
  )
}
